/* ──────────────────────────────────────────────────────────────────────────────
 * Service Worker Registration
 *
 * Update detection uses TWO independent mechanisms to be bulletproof:
 *
 *   1. Registration-based: updatefound → installed state → callback fires
 *   2. On-load check: reg.waiting exists → update already installed
 *
 * The SW sends "SW_UPDATED" from its activate handler as a third safety net.
 * ────────────────────────────────────────────────────────────────────────────── */

const SW_URL = '/sw.js';
const SW_SCOPE = '/';

let registration: ServiceWorkerRegistration | null = null;

/**
 * Register sw.js on page load.
 */
export function registerSW() {
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', async () => {
    try {
      registration = await navigator.serviceWorker.register(SW_URL, {
        scope: SW_SCOPE,
      });
      console.log('[SW-Reg] Registered with scope:', registration.scope);

      // ── Mechanism 1: Listen for updatefound ──
      registration.addEventListener('updatefound', () => {
        const newWorker = registration?.installing;
        if (!newWorker) return;

        console.log('[SW-Reg] New SW installing...');

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New SW installed, old one still active → UPDATE AVAILABLE
              console.log('[SW-Reg] ✅ Update ready — new SW installed');
              // Dispatch event for React to pick up
              window.dispatchEvent(new Event('sw-update-ready'));
            } else {
              // First install (no controller yet) → just cache, no banner
              console.log('[SW-Reg] Content cached for offline use');
            }
          }
        });
      });

      // ── Mechanism 2: Check for waiting SW immediately ──
      // Handles case where update was installed during a previous page view
      if (registration.waiting && navigator.serviceWorker.controller) {
        console.log('[SW-Reg] ✅ Waiting SW found on load — update ready');
        window.dispatchEvent(new Event('sw-update-ready'));
      }

      // Trigger update check (browser compares SW byte hashes)
      registration.update().catch(() => {});

    } catch (error) {
      console.error('[SW-Reg] Registration failed:', error);
    }
  });
}

/**
 * Tell the waiting SW to skip the waiting phase and take control immediately.
 * After skipWaiting, the SW's activate handler sends "SW_UPDATED" to all clients.
 */
export function applyUpdate() {
  if (!navigator.serviceWorker.controller) return;

  const waiting = registration?.waiting;
  if (waiting) {
    console.log('[SW-Reg] Sending SKIP_WAITING to new SW');
    waiting.postMessage({ type: 'SKIP_WAITING' });
  }
}

/**
 * Start periodic SW update checks (every 60 minutes).
 * When a new SW is found, the callback fires.
 */
export function startPeriodicSWChecks() {
  if (!('serviceWorker' in navigator)) return;

  setInterval(() => {
    if (!registration) return;
    console.log('[SW-Reg] Periodic update check');
    registration.update().catch(() => {});
  }, 60 * 60 * 1000);
}

/**
 * Check if the app is currently offline.
 */
export function isOffline(): boolean {
  return !navigator.onLine;
}
